"use strict";

exports.__esModule = true;
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _clsx = _interopRequireDefault(require("clsx"));
var _reactTable = require("@tanstack/react-table");
var _core = require("@dnd-kit/core");
var _sortable = require("@dnd-kit/sortable");
var _modifiers = require("@dnd-kit/modifiers");
var _useUserData = _interopRequireDefault(require("../../hooks/useUserData"));
var _DataTable = require("./DataTable2.scroll");
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
const DraggableRow = _ref => {
  let {
    row,
    renderRowSubComponent,
    dragAnchor
  } = _ref;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = (0, _sortable.useSortable)({
    id: row.id
  });
  const style = transform ? {
    transform: "translate3d(" + transform.x + "px, " + transform.y + "px, 0)",
    transition,
    zIndex: isDragging ? 10 : 0,
    boxShadow: isDragging ? '0 2px 5px hsl(0deg 0% 50% / 35%)' : null,
    touchAction: 'none',
    backgroundColor: isDragging ? 'white' : null
  } : null;
  return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement("tr", {
    ref: setNodeRef,
    style: style,
    className: "uk-datatable__tr uk-datatable__tr--draggable position-relative"
  }, /*#__PURE__*/_react.default.createElement("td", {
    className: "uk-datatable__td uk-datatable__td--draggable uk-datatable__actions"
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-datatable__td-inner"
  }, /*#__PURE__*/_react.default.createElement("span", _extends({}, attributes, listeners, {
    style: {
      cursor: isDragging ? 'grabbing' : 'grab'
    }
  }), dragAnchor))), row.getVisibleCells().map(cell => {
    var _cell$column$columnDe, _cell$column$columnDe2;
    return /*#__PURE__*/_react.default.createElement("td", {
      key: cell.id,
      style: (_cell$column$columnDe = cell.column.columnDef) != null && (_cell$column$columnDe = _cell$column$columnDe.meta) != null && _cell$column$columnDe.flexibleCol ? {
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: cell.column.getSize()
      } : {
        width: cell.column.getSize()
      },
      className: (0, _clsx.default)('uk-datatable__td', (_cell$column$columnDe2 = cell.column.columnDef) == null || (_cell$column$columnDe2 = _cell$column$columnDe2.meta) == null ? void 0 : _cell$column$columnDe2.className)
    }, /*#__PURE__*/_react.default.createElement("div", {
      className: "uk-datatable__td-inner"
    }, (0, _reactTable.flexRender)(cell.column.columnDef.cell, cell.getContext())));
  })), row.getIsExpanded() && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement("tr", null), /*#__PURE__*/_react.default.createElement("tr", {
    className: (0, _clsx.default)('uk-datatable__tr', 'is-expanded', 'uk-datatable__tr--subRow')
  }, /*#__PURE__*/_react.default.createElement("td", {
    colSpan: row.getVisibleCells().length
  }, renderRowSubComponent({
    row
  })))));
};
const ReorderTable = _ref2 => {
  let {
    id: dataTableId,
    columns,
    data: initialData,
    initialState = {},
    stripped,
    hoverable,
    bordered,
    subComponent,
    className,
    children,
    stickyRowPosition,
    displayDebug = false,
    dragAnchor,
    onReorder
  } = _ref2;
  const tableRef = _react.default.useRef(null);
  const tableWrapperRef = _react.default.useRef(null);
  const tableTopScrollbarRef = _react.default.useRef(null);
  const tableBottomScrollbarRef = _react.default.useRef(null);
  const {
    handleGrab,
    handleRelease,
    handleMove
  } = (0, _DataTable.useScroll)(tableWrapperRef, tableTopScrollbarRef, tableBottomScrollbarRef);
  const [data, setData] = _react.default.useState(initialData);
  _react.default.useEffect(() => setData(initialData), [initialData]);
  const [columnSizing, setColumnSizing] = (0, _useUserData.default)(dataTableId, 'columnSizing', initialState.columnSizing || []);
  const table = (0, _reactTable.useReactTable)({
    data,
    columns,
    defaultColumn: {
      size: 160,
      minSize: 80,
      maxSize: Number.MAX_SAFE_INTEGER
    },
    initialState: {
      ...initialState
    },
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    onColumnSizingChange: setColumnSizing,
    enableExpanding: true,
    state: {
      columnSizing
    },
    getCoreRowModel: (0, _reactTable.getCoreRowModel)(),
    getExpandedRowModel: (0, _reactTable.getExpandedRowModel)(),
    debugTable: displayDebug,
    debugHeaders: displayDebug,
    debugColumns: displayDebug
  });
  const renderRowSubComponent = _react.default.useCallback(_ref3 => {
    let {
      row
    } = _ref3;
    return subComponent && typeof subComponent === 'function' && subComponent(row);
  }, [subComponent]);
  const isFlexible = table.options.columns.filter(col => {
    var _col$meta;
    return col == null || (_col$meta = col.meta) == null ? void 0 : _col$meta.flexibleCol;
  }).length > 0;
  const sensors = (0, _core.useSensors)((0, _core.useSensor)(_core.PointerSensor), (0, _core.useSensor)(_core.KeyboardSensor, {
    coordinateGetter: _sortable.sortableKeyboardCoordinates
  }));
  return /*#__PURE__*/_react.default.createElement("section", {
    className: (0, _clsx.default)('uk-datatable', className)
  }, displayDebug && /*#__PURE__*/_react.default.createElement("pre", null, JSON.stringify(table.getState(), null, 2)), children, /*#__PURE__*/_react.default.createElement("div", {
    ref: tableTopScrollbarRef,
    className: "uk-datatable__scrollbar"
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-datatable__scrollbar-thumb",
    style: {
      width: tableWrapperRef.current ? tableWrapperRef.current.scrollWidth + 'px' : 0
    }
  })), /*#__PURE__*/_react.default.createElement("div", {
    ref: tableWrapperRef,
    className: "uk-datatable__table"
  }, /*#__PURE__*/_react.default.createElement(_core.DndContext, {
    sensors: sensors,
    modifiers: [_modifiers.restrictToVerticalAxis],
    onDragEnd: e => {
      const {
        active,
        over
      } = e;
      if (active.id !== over.id) {
        const newOrder = (0, _sortable.arrayMove)(data, active.id, over.id);
        setData(newOrder);
        if (typeof onReorder === 'function') {
          onReorder(newOrder);
        }
      }
    }
  }, /*#__PURE__*/_react.default.createElement(_sortable.SortableContext, {
    items: table.getRowModel().rows,
    strategy: _sortable.verticalListSortingStrategy
  }, /*#__PURE__*/_react.default.createElement("table", {
    ref: tableRef,
    id: dataTableId,
    className: (0, _clsx.default)('uk-table', isFlexible && 'uk-table--wide', stripped && 'uk-table--stripped', hoverable && 'uk-table--hoverable', bordered && 'uk-table--bordered'),
    onMouseDown: e => handleGrab(e, tableWrapperRef),
    onMouseUp: e => handleRelease(e, tableWrapperRef),
    onMouseLeave: e => handleRelease(e, tableWrapperRef),
    onMouseMove: e => handleMove(e, tableWrapperRef)
  }, /*#__PURE__*/_react.default.createElement("thead", {
    className: "uk-datatable__thead"
  }, table.getHeaderGroups().map(headerGroup => /*#__PURE__*/_react.default.createElement("tr", {
    key: headerGroup.id,
    className: "uk-datatable__tr"
  }, /*#__PURE__*/_react.default.createElement("th", {
    className: "uk-datatable__th  uk-datatable__th--draggable"
  }), headerGroup.headers.map((header, headerIdx) => {
    var _header$column$column, _header$column$column2;
    return /*#__PURE__*/_react.default.createElement("th", {
      key: header.id,
      colSpan: header.colSpan,
      style: (_header$column$column = header.column.columnDef) != null && (_header$column$column = _header$column$column.meta) != null && _header$column$column.flexibleCol ? {
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: header.getSize()
      } : {
        width: header.getSize()
      },
      className: (0, _clsx.default)('uk-datatable__th', (_header$column$column2 = header.column.columnDef) == null || (_header$column$column2 = _header$column$column2.meta) == null ? void 0 : _header$column$column2.headerClassName, header.column.getCanResize() && 'position-relative', data.length > 1 && [header.column.getCanSort() && 'uk-datatable__th--sortable', header.column.getIsSorted() && "uk-datatable__th--sorted-" + header.column.getIsSorted()]),
      onMouseUp: header.column.getIsResizing() ? null : header.column.getToggleSortingHandler()
    }, header.isPlaceholder ? null : /*#__PURE__*/_react.default.createElement("span", null, (0, _reactTable.flexRender)(header.column.columnDef.header, header.getContext())), header.column.getCanResize() && /*#__PURE__*/_react.default.createElement("div", {
      onMouseDown: header.getResizeHandler(),
      onTouchStart: header.getResizeHandler(),
      onClick: event => event.stopPropagation(),
      className: (0, _clsx.default)('uk-datatable__resizer', header.column.getIsResizing() && 'uk-datatable__resizer--isResizing')
    }));
  })))), /*#__PURE__*/_react.default.createElement("tbody", {
    className: "uk-datatable__tbody"
  }, stickyRowPosition !== 'bottom' && table.getFooterGroups()[0].headers.filter(f => f.column.columnDef.footer).length > 0 // Is there a sticky/footer definition in a column?
  && table.getRowModel().rows.length > 0 // Is there any row ?
  && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, table.getFooterGroups().map(headerGroup => /*#__PURE__*/_react.default.createElement("tr", {
    key: headerGroup.id,
    className: "uk-datatable__tr uk-datatable__tr--sticky"
  }, headerGroup.headers.map((header, headerIdx) => {
    var _header$column$column3, _header$column$column4;
    return /*#__PURE__*/_react.default.createElement("td", {
      key: header.id,
      colSpan: header.colSpan,
      style: (_header$column$column3 = header.column.columnDef) != null && (_header$column$column3 = _header$column$column3.meta) != null && _header$column$column3.flexibleCol ? {
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: header.getSize()
      } : {
        width: header.getSize()
      },
      className: (0, _clsx.default)('uk-datatable__td', (_header$column$column4 = header.column.columnDef) == null || (_header$column$column4 = _header$column$column4.meta) == null ? void 0 : _header$column$column4.stickyRowClassName)
    }, /*#__PURE__*/_react.default.createElement("span", null, (0, _reactTable.flexRender)(header.column.columnDef.footer, header.getContext())));
  })))), table.getRowModel().rows.map((row, i) => {
    return /*#__PURE__*/_react.default.createElement(DraggableRow, {
      row: row,
      key: row.id,
      renderRowSubComponent: renderRowSubComponent,
      dragAnchor: dragAnchor
    });
  }), stickyRowPosition !== 'top' && table.getFooterGroups()[0].headers.filter(f => f.column.columnDef.footer).length > 0 // Is there a sticky/footer definition in a column?
  && table.getRowModel().rows.length > 0 // Is there any row ?
  && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, table.getFooterGroups().map(headerGroup => /*#__PURE__*/_react.default.createElement("tr", {
    key: headerGroup.id,
    className: "uk-datatable__tr uk-datatable__tr--sticky"
  }, headerGroup.headers.map((header, headerIdx) => {
    var _header$column$column5, _header$column$column6;
    return /*#__PURE__*/_react.default.createElement("td", {
      key: header.id,
      colSpan: header.colSpan,
      style: (_header$column$column5 = header.column.columnDef) != null && (_header$column$column5 = _header$column$column5.meta) != null && _header$column$column5.flexibleCol ? {
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: header.getSize()
      } : {
        width: header.getSize()
      },
      className: (0, _clsx.default)('uk-datatable__td', (_header$column$column6 = header.column.columnDef) == null || (_header$column$column6 = _header$column$column6.meta) == null ? void 0 : _header$column$column6.stickyRowClassName)
    }, /*#__PURE__*/_react.default.createElement("span", null, (0, _reactTable.flexRender)(header.column.columnDef.footer, header.getContext())));
  }))))))))), /*#__PURE__*/_react.default.createElement("div", {
    ref: tableBottomScrollbarRef,
    className: "uk-datatable__scrollbar"
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-datatable__scrollbar-thumb",
    style: {
      width: tableWrapperRef.current ? tableWrapperRef.current.scrollWidth + 'px' : 0
    }
  })));
};
ReorderTable.propTypes = {
  /**
   * ID of the Switch (used to bind the label to the Switch)
   * NB: the ID will be randomly generated if not set.
   */
  id: _propTypes.default.string.isRequired,
  /**
   * ID of the Switch (used to bind the label to the Switch)
   * NB: the ID will be randomly generated if not set.
   */
  columns: _propTypes.default.any,
  /**
   * ID of the Switch (used to bind the label to the Switch)
   * NB: the ID will be randomly generated if not set.
   */
  data: _propTypes.default.any,
  /**
   * ID of the Switch (used to bind the label to the Switch)
   * NB: the ID will be randomly generated if not set.
   */
  initialState: _propTypes.default.any,
  /**
   * ID of the Switch (used to bind the label to the Switch)
   * NB: the ID will be randomly generated if not set.
   */
  stripped: _propTypes.default.bool,
  /**
   * ID of the Switch (used to bind the label to the Switch)
   * NB: the ID will be randomly generated if not set.
   */
  hoverable: _propTypes.default.bool,
  /**
   * ID of the Switch (used to bind the label to the Switch)
   * NB: the ID will be randomly generated if not set.
   */
  bordered: _propTypes.default.bool,
  /**
   * Write 'top' to switch the scrollbar position
   * NB: 'bottom' default position
   */
  stickyRowPosition: _propTypes.default.oneOf(['top', 'bottom', 'both']),
  /**
   * ID of the Switch (used to bind the label to the Switch)
   * NB: the ID will be randomly generated if not set.
   */
  subComponent: _propTypes.default.any
};
ReorderTable.defaultProps = {
  initialState: {},
  stripped: false,
  hoverable: false,
  bordered: false,
  stickyRowPosition: 'bottom',
  dragAnchor: /*#__PURE__*/_react.default.createElement("span", {
    className: "uk-button uk-bghcolor--white tiny"
  }, /*#__PURE__*/_react.default.createElement("i", {
    className: "fa fa-grip-lines"
  }))
};
var _default = exports.default = ReorderTable;